import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { UiTooltipDirective } from '../../directives/tooltip/tooltip.directive';
import type {
  UiFlowMarkerTone,
  UiFlowMarkerVariant,
} from '../../shared/flow/flow.types';

@Component({
  selector: 'ui-flow-marker',
  standalone: true,
  imports: [UiTooltipDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (variant() !== 'none') {
      <span
        class="ui-flow-marker"
        [class]="markerClass()"
        [style.color]="toneStyle().color"
        [style.background]="toneStyle().background"
        [style.border-color]="toneStyle().borderColor"
        [attr.aria-label]="ariaLabel()"
        [attr.tabindex]="showTooltip() ? 0 : null"
        [uiTooltip]="tooltipText() ?? ''"
        [uiTooltipDisabled]="!showTooltip()"
      >
        @if (isText()) {
          {{ tooltipText() }}
        } @else {
          {{ symbol() }}
        }
      </span>
    }
  `,
  styles: `
    :host { display: inline-flex; max-width: 100%; }
    .ui-flow-marker {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      box-sizing: border-box;
      font-family: var(--font-sans, inherit);
      font-weight: 700;
      line-height: 1.2;
      text-align: center;
    }
    .ui-flow-marker--icon {
      width: 22px;
      height: 22px;
      border-radius: 50%;
      border: 1.5px solid transparent;
      font-size: 0.7rem;
    }
    .ui-flow-marker--text {
      min-height: 22px;
      max-width: 140px;
      padding: 2px 8px;
      border-radius: 6px;
      border: 0;
      background: #fff;
      font-size: 0.75rem;
      font-weight: 650;
      word-break: break-word;
    }
    .ui-flow-marker--success.ui-flow-marker--icon {
      background: color-mix(in srgb, #16a34a 14%, #fff);
      border-color: color-mix(in srgb, #16a34a 45%, #fff);
      color: #15803d;
    }
    .ui-flow-marker--error.ui-flow-marker--icon {
      background: color-mix(in srgb, #dc2626 12%, #fff);
      border-color: color-mix(in srgb, #dc2626 40%, #fff);
      color: #b91c1c;
    }
    .ui-flow-marker--neutral.ui-flow-marker--icon {
      background: #f1f5f9;
      border-color: #e4e4e6;
      color: #64748b;
    }
    .ui-flow-marker--success.ui-flow-marker--text {
      color: #15803d;
    }
    .ui-flow-marker--error.ui-flow-marker--text {
      color: #b91c1c;
    }
    .ui-flow-marker--neutral.ui-flow-marker--text {
      color: #334155;
    }
  `,
})
export class UiFlowMarkerComponent {
  readonly tone = input<UiFlowMarkerTone>('neutral');
  readonly variant = input<UiFlowMarkerVariant>('icon');
  /** En variant icon: tooltip. En variant text: texto visible centrado. */
  readonly tooltipText = input<string | null>(null);

  protected readonly isText = computed(() => this.variant() === 'text');
  protected readonly showTooltip = computed(
    () => this.variant() === 'icon' && !!this.tooltipText(),
  );

  protected readonly markerClass = computed(
    () =>
      `ui-flow-marker--${this.variant()} ui-flow-marker--${this.tone()}`,
  );

  /** Color en línea: dentro del foreignObject del diagrama la clase no basta. */
  protected readonly toneStyle = computed(() => {
    const tone = this.tone();
    if (this.variant() === 'text') {
      const color =
        tone === 'success' ? '#15803d' : tone === 'error' ? '#b91c1c' : '#334155';
      return { color, background: '#fff', borderColor: 'transparent' };
    }
    if (tone === 'success') {
      return {
        color: '#15803d',
        background: 'color-mix(in srgb, #16a34a 14%, #fff)',
        borderColor: 'color-mix(in srgb, #16a34a 45%, #fff)',
      };
    }
    if (tone === 'error') {
      return {
        color: '#b91c1c',
        background: 'color-mix(in srgb, #dc2626 12%, #fff)',
        borderColor: 'color-mix(in srgb, #dc2626 40%, #fff)',
      };
    }
    return {
      color: '#64748b',
      background: '#f1f5f9',
      borderColor: '#e4e4e6',
    };
  });

  protected readonly symbol = computed(() => {
    if (this.tone() === 'success') return '✓';
    if (this.tone() === 'error') return '✕';
    return '•';
  });

  protected readonly ariaLabel = computed(() => {
    if (this.tooltipText()) return this.tooltipText()!;
    if (this.tone() === 'success') return 'Camino exitoso';
    if (this.tone() === 'error') return 'Camino de error';
    return 'Camino';
  });
}
